import { AUTOSAVE_TIMEOUT, SCROLL } from '@opengov/stories-shared/constants';
import {
  INewStoryContent,
  IProgressTracker,
  IStory,
  IStoryContent,
  IStoryCustomColors
} from '@opengov/stories-shared/types';
import { mergeListsById } from '@opengov/stories-shared/utils/general';
import { selectStoryContentFormats } from '@opengov/stories-shared/utils/storyContents';
import cx from 'classnames2';
import { debounce, isNil, throttle } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  IEditorMessages,
  IModalMessages,
  ISettings,
  ISettingsColors,
  IStoryEditorContentSpacing,
  IUpdateCustomColorProps,
  IUpdateStoryContentProps,
  SaveAllStoryContentsSuccessAction,
  SaveStoryContentSuccessAction,
  UpdateCustomColorsResponse,
  UpdateMetaStoryContentAction,
  UpdateMetaStoryContentInViewAction,
  UpdateMetaStoryContentOrderAction,
  UpdateStoryContentResponse
} from '../../../../../types';
import { ADD_BACKGROUND, ADD_CONTENT, ADD_EMBED, ADD_IMAGE, ADD_TABLE_TILE, ADD_TILE, ADD_VIDEO, DELETE_CONTENT } from '../../../../constants';
import { useEditorModal } from '../../../StoryView/StoryView/contexts/EditorModalContext';
import { useModal } from '../../../StoryView/StoryView/contexts/ModalContext';
import {
  addSlide,
  addUuidToEditables,
  clearStoryContent,
  IFonts,
  removeSlide,
  removeUuidFromEditables,
  storyEmbedsToRemove,
  updateColor,
  updateEditableByUuid,
  updateSpacing,
  updateSpacingDefaults
} from '../../../../utils/dom';
import { getSubtypeColorsByStoryContentType } from '../../../../utils/editor';

import { AddContent } from '../../AddContent';
import { ContentMenu } from '../../ContentMenu';
import { AddTileContainer } from '../../../../../modals/components/AddTile/AddTileContainer';
import { AddTableTileContainer } from '../../../../../modals/components/AddTableTile/AddTableTileContainer';

import styles from './TinyMCEStoryEditor.scss';
import {
  AddMediaHandler,
  AiSummaryResult,
  EditorValueSource,
  formatStoryContent,
  getMediaHandlers,
  getStoryContentsNextToNewStoryContent,
  getVisibleStoryContentId,
  moveStoryContent,
  parseHTMLString,
  scrollToStoryContent
} from './tinyMCEStoryEditorUtils';
import { getMediaIndex } from '../../../../utils/mediaIndex.utils';

export type ITinyMCEStoryEditorBaseProps = {
  story: IStory;
  canAddProposalContent: boolean;
  storyContents: IStoryContent[];
  storyCustomColors: IStoryCustomColors;
  isTemplate: boolean;
  canEditPageBreaks: boolean;
  canEditSpacing: boolean;
  canUseAiToGenerateContent: boolean;
  canUseStoriesSummarizerGA: boolean;
  canUseOgAssistGenerateSummary: boolean;
  entityName: string;
  entityId: number;
  isAdmin?: boolean;
  settings: ISettings;
  messages: IEditorMessages;
  userId?: number;
  updateStoryContent(storyContent: IStoryContent | INewStoryContent): SaveStoryContentSuccessAction;
  updateStoryContents(storyContents: IStoryContent[]): SaveAllStoryContentsSuccessAction;
  updateStoryContentDB?(props: IUpdateStoryContentProps): Promise<UpdateStoryContentResponse> | void;
  updateAllStoryContentsDB?(props: {
    isTemplate: boolean;
    story: IStory;
    storyContents: IStoryContent[];
  }): Promise<UpdateStoryContentResponse[]> | void;
  updateMetaStoryContent(storyContent: IStoryContent): UpdateMetaStoryContentAction;
  updateStoryCustomColors(props: IUpdateCustomColorProps): UpdateCustomColorsResponse;
  updateMetaStoryContentOrder(order: number): UpdateMetaStoryContentOrderAction;
  updateMetaStoryContentInView(id: string): UpdateMetaStoryContentInViewAction;
  openModal(modal: string): IAction<string, string>;
  updatePublicationUpdatedAt?(props: { publicationId: string; userId: number }): Promise<void>;
  deleteStoryEmbed?(props: { storyId: string; embedId: string }): Promise<void>;
  showProgressTracker: boolean;
  modalMessages: IModalMessages;
  saveProgressTrackerConfig(props: IProgressTracker): Promise<void>;
};

export type ITinyMCEStoryEditorProps = {
  totalReportTables: number;
  isReadOnlySection: boolean;
  isReadOnlyMode: boolean;
  isLast: boolean;
  storyContent: IStoryContent;
  totalStoryContents: number;
  nextStoryContentOrder: number;
  entitySubdomain: string;
  document: Document;
  window: Window;
  publicationId?: string;
  isAiEnabledByAdmin: boolean;
  isCustomSpreadsheetReportEnabled: boolean;
} & ITinyMCEStoryEditorBaseProps;

export const TinyMCEStoryEditor = React.memo((props: ITinyMCEStoryEditorProps) => {
  const {
    window,
    document,
    story,
    isTemplate,
    storyContents,
    isReadOnlyMode,
    storyContent,
    messages,
    entityName,
    totalReportTables,
    entitySubdomain,
    settings,
    storyCustomColors,
    canAddProposalContent,
    deleteStoryEmbed,
    canUseAiToGenerateContent,
    canUseStoriesSummarizerGA,
    canUseOgAssistGenerateSummary,
    entityId,
    showProgressTracker,
    modalMessages,
    updateStoryContent,
    saveProgressTrackerConfig,
    updateStoryContentDB,
    isAiEnabledByAdmin,
    isCustomSpreadsheetReportEnabled
  } = props;

  const prevStoryContent = useRef<IStoryContent>(storyContent);
  const prevTotalReportTables = useRef<number>(totalReportTables);
  const prevIncomingContent = useRef<string>(storyContent.content);
  const timeout = useRef<NodeJS.Timer | null>(null);
  const autoSaveTimer = useRef<NodeJS.Timer | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isReadOnlySection, setIsReadOnlySection] = useState<boolean>(props.isReadOnlySection);
  const [content, setContent] = useState<string>(addUuidToEditables(storyContent.content) || messages.noContentError);
  // Keep latest content in a ref so the sync effect need not depend on it.
  const contentRef = useRef<string>(content);
  contentRef.current = content;
  const [contentSeed, setContentSeed] = useState<number>(0);
  const fontColors = useMemo(
    () =>
      getSubtypeColorsByStoryContentType({
        storyContentType: storyContent.type,
        colors: settings.brand.colors,
        storyCustomColors
      }),
    [storyContent.type, settings.brand.colors, storyCustomColors]
  );

  const { openDeleteContent, openAddBackground, openAddImage, openAddVideo, openAddEmbed, openAddContent } = useEditorModal();
  const { setCurrentModal } = useModal();

  const handleUpdateModal = (modal: string, newStoryContent: IStoryContent) => {
    if (modal === DELETE_CONTENT) {
      openDeleteContent(newStoryContent);
    } else if (modal === ADD_BACKGROUND) {
      openAddBackground(newStoryContent);
    } else if (modal === ADD_IMAGE) {
      openAddImage(newStoryContent);
    } else if (modal === ADD_VIDEO) {
      openAddVideo(newStoryContent);
    } else if (modal === ADD_EMBED) {
      openAddEmbed(newStoryContent);
    } else if (modal === ADD_CONTENT) {
      openAddContent();
    } else if (modal === ADD_TILE) {
      setCurrentModal(
        <AddTileContainer
          isTemplate={isTemplate}
          entityId={entityId}
          entitySubdomain={entitySubdomain}
          story={story}
          storyContents={storyContents}
          settings={settings}
          messages={modalMessages}
          storyContent={newStoryContent}
          selectedEmbed={null}
          isReportEmbedListSelected={false}
          publicationId={props.publicationId}
          isAdmin={props.isAdmin ?? false}
          onClose={() => setCurrentModal(null)}
        />
      );
    } else if (modal === ADD_TABLE_TILE) {
      setCurrentModal(
        <AddTableTileContainer
          isTemplate={isTemplate}
          entityId={entityId}
          entitySubdomain={entitySubdomain}
          story={story}
          storyContents={storyContents}
          messages={modalMessages}
          storyContent={newStoryContent}
          selectedEmbed={null}
          isReportEmbedListSelected={false}
          publicationId={props.publicationId}
          onClose={() => setCurrentModal(null)}
        />
      );
    } else {
      props.updateMetaStoryContent(newStoryContent);
      props.openModal(modal);
    }
  };

  const handleAddMedia: AddMediaHandler = (event, modal) => {
    event.preventDefault();
    const mediaIndex = getMediaIndex(event.target as HTMLElement);
    handleUpdateModal(modal, { ...storyContent, content: removeUuidFromEditables(content), mediaIndex });
  };

  const handleEditMedia = useCallback(
    (modal: string, mediaIndex: number) => {
      handleUpdateModal(modal, { ...storyContent, content: removeUuidFromEditables(content), mediaIndex });
    },
    [storyContent, content]
  );

  const updateUpdatedAtOfPublication = () => {
    const { publicationId, userId } = props;
    if (publicationId && userId) void props.updatePublicationUpdatedAt?.({ publicationId, userId });
  };

  const onUpdateStoryContent = debounce((updatedStoryContent: Partial<IStoryContent>) => {
    const newStoryContent = { ...storyContent, ...updatedStoryContent };
    props.updateStoryContent(newStoryContent);
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      void props.updateStoryContentDB({ isTemplate, story, storyContent: newStoryContent });
      updateUpdatedAtOfPublication();
    }, AUTOSAVE_TIMEOUT);
  }, 500);

  const onUpdateStoryContents = debounce((updatedStoryContents: IStoryContent[]) => {
    props.updateStoryContents(mergeListsById(storyContents, updatedStoryContents));
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      props.updateAllStoryContentsDB({ isTemplate, story, storyContents: updatedStoryContents });
      updateUpdatedAtOfPublication();
    }, AUTOSAVE_TIMEOUT);
  }, 500);

  const handleAddModal = (order: number) => {
    const newStoryContents = getStoryContentsNextToNewStoryContent(order, storyContents);
    onUpdateStoryContents(newStoryContents);
    props.updateMetaStoryContentOrder(order);
    handleUpdateModal(ADD_CONTENT, storyContent);
  };

  const [editorValueSource, setEditorValueSource] = React.useState<EditorValueSource>({
    variant: 'user-editing'
  });

  const handleGenerateAiSummary = useCallback(
    ({ editorUuid, status, value }: AiSummaryResult) => {
      if (!isNil(value)) {
        const fragment = document.createRange().createContextualFragment(content);
        const editableElement = fragment.querySelector(`.editable[data-uuid="${editorUuid}"]`);

        const newContent = updateEditableByUuid(content, editorUuid, editableElement.innerHTML + '<br>' + value);

        if (newContent !== content) {
          onUpdateStoryContent({ content: removeUuidFromEditables(newContent), shouldUpdate: false });
          setContent(newContent);
        }
      }

      setEditorValueSource({
        variant: 'ai-editing',
        status,
        editorUuid,
        resetValueSourceToUser: () => setEditorValueSource({ variant: 'user-editing' })
      });
    },
    [content]
  );

  const onChange = useCallback(
    (value: string, uuid: string) => {
      const newContent = updateEditableByUuid(content, uuid, value);
      if (newContent !== content) {
        // Clear shouldUpdate so typing does not re-assert a pending re-seed.
        onUpdateStoryContent({ content: removeUuidFromEditables(newContent), shouldUpdate: false });
        setContent(newContent);
      }
    },
    [content]
  );

  const handleRemoveSlide = useCallback(
    (mediaIndex: number) => {
      const newStoryContent = removeSlide(storyContent, mediaIndex);
      onUpdateStoryContent(newStoryContent);
    },
    [storyContent]
  );
  const mediaHandlers = useMemo(() => getMediaHandlers(handleAddMedia), [handleAddMedia]);
  const parsedElements = useMemo(
    () =>
      parseHTMLString(content, {
        window,
        document,
        messages,
        storyContent,
        mediaHandlers,
        readOnly: isReadOnlySection,
        totalReportTables,
        onChange,
        handleRemoveSlide,
        handleEditMedia,
        entitySubdomain,
        fontColors,
        handleGenerateAiSummary,
        editorValueSource,
        canUseAiToGenerateContent,
        canUseStoriesSummarizerGA,
        canUseOgAssistGenerateSummary,
        isAiEnabledByAdmin,
        entityId,
        showProgressTracker,
        modalMessages,
        updateStoryContent,
        updateStoryContentDB,
        saveProgressTrackerConfig,
        story,
        isTemplate,
        publicationId: props.publicationId,
        isCustomSpreadsheetReportEnabled,
        contentSeed
      }),
    [
      content,
      contentSeed,
      window,
      document,
      messages,
      storyContent,
      mediaHandlers,
      isReadOnlySection,
      totalReportTables,
      onChange,
      handleRemoveSlide,
      handleEditMedia,
      entitySubdomain,
      fontColors,
      handleGenerateAiSummary,
      editorValueSource,
      canUseAiToGenerateContent,
      canUseStoriesSummarizerGA,
      canUseOgAssistGenerateSummary,
      entityId,
      showProgressTracker,
      modalMessages,
      saveProgressTrackerConfig,
      story,
      isTemplate,
      updateStoryContent,
      updateStoryContentDB
    ]
  );
  const handleScroll = throttle(() => {
    const visibleStoryContentId = getVisibleStoryContentId(document);
    if (visibleStoryContentId) props.updateMetaStoryContentInView(visibleStoryContentId);
  }, 250);

  const handleOrderClick = (up: boolean) => {
    const newStoryContents = moveStoryContent(up, storyContent, storyContents);
    onUpdateStoryContents(newStoryContents);
    timeout.current = setTimeout(() => {
      scrollToStoryContent(document, storyContent._id);
    }, 100);
  };

  const handleFormatClick = async (format: string, fonts: IFonts, colors: ISettingsColors): Promise<void> => {
    const newStoryContent = formatStoryContent(storyContent, entityName, format, fonts, colors);
    const removedStoryEmbeds = storyEmbedsToRemove(storyContent.content, newStoryContent.content);
    if (removedStoryEmbeds.length > 0)
      try {
        await Promise.all(
          removedStoryEmbeds?.map(async (embedId) => {
            return deleteStoryEmbed({
              storyId: storyContent.story as string,
              embedId
            });
          })
        );
      } catch (e) {
        // Logging to be added
      }
    onUpdateStoryContent(newStoryContent);
  };

  const handleColorClick = (event: React.MouseEvent<HTMLElement>, color: string) => {
    event.preventDefault();
    const updatedStoryContent = updateColor({ ...storyContent, content: removeUuidFromEditables(content) }, color);
    setContent(addUuidToEditables(updatedStoryContent.content));
    onUpdateStoryContent(updatedStoryContent);
  };

  const handleSaveSpacing = (spacing: IStoryEditorContentSpacing) => {
    const newStoryContent = updateSpacing({ ...storyContent, content: removeUuidFromEditables(content) }, spacing);
    onUpdateStoryContent(updateSpacingDefaults(newStoryContent, story));
  };

  const handleAddSlide = () => {
    const newStoryContent = addSlide(storyContent);
    onUpdateStoryContent(newStoryContent);
  };

  useEffect(() => {
    handleScroll();
    window.addEventListener(SCROLL, handleScroll);
    // Delay rendering so window object is available to Editor
    timeout.current = setTimeout(() => setIsLoading(false), 100);
    return () => {
      window.removeEventListener(SCROLL, handleScroll);
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, []);

  // Keep read-only in sync when switching view ↔ edit (same mounted route).
  useEffect(() => {
    setIsReadOnlySection(props.isReadOnlySection);
  }, [props.isReadOnlySection]);

  useEffect(() => {
    const shouldSetContent =
      prevStoryContent.current.format !== storyContent.format || prevTotalReportTables.current !== totalReportTables;
    if (shouldSetContent) {
      prevStoryContent.current = storyContent;
      prevTotalReportTables.current = totalReportTables;
      setContent(addUuidToEditables(storyContent.content) || messages.noContentError);
    }
  }, [prevStoryContent.current, prevTotalReportTables.current, storyContent.format, totalReportTables]);

  useEffect(() => {
    // Re-seed only on real programmatic edits (shouldUpdate), not autosave echoes.
    const hasPendingReseed =
      storyContent.shouldUpdate && removeUuidFromEditables(contentRef.current) !== storyContent.content;
    if (storyContent.content === prevIncomingContent.current && !hasPendingReseed) return;
    prevIncomingContent.current = storyContent.content;
    if (hasPendingReseed) {
      prevStoryContent.current = storyContent;
      setContent(addUuidToEditables(storyContent.content) || messages.noContentError);
      setContentSeed((seed) => seed + 1);
      props.updateStoryContent({ ...storyContent, shouldUpdate: false });
    }
  }, [storyContent.shouldUpdate, storyContent.content]);

  useEffect(() => {
    if (storyContent.shouldClear) {
      const newContent = clearStoryContent(storyContent.content);
      setContent(newContent);
      onUpdateStoryContent({ ...storyContent, content: newContent, shouldClear: false });
    }
  }, [storyContent.shouldClear, storyContent.content]);

  useEffect(() => {
    if (storyContent.scrollTo) {
      scrollToStoryContent(document, storyContent._id);
      props.updateStoryContent({ ...storyContent, scrollTo: false });
    }
  }, [storyContent.scrollTo, document, storyContent._id]);

  const handlePreviewSection = () => {
    setIsReadOnlySection((prevValue) => !prevValue);
  };

  if (isLoading) return null;
  return (
    <div className={cx(styles.storyContentSection, 'story-content-section')} data-test="story-content-section">
      <div className={cx({ hidden: !storyContent.active })} suppressContentEditableWarning={true}>
        {parsedElements}
        <ContentMenu
          canEditSpacing={props.canEditSpacing}
          canEditPageBreaks={props.canEditPageBreaks}
          totalStoryContents={props.totalStoryContents}
          settings={settings}
          storyCustomColors={storyCustomColors}
          updateStoryCustomColors={props.updateStoryCustomColors}
          window={window}
          document={document}
          isTemplate={isTemplate}
          isReadOnlySection={isReadOnlySection}
          canAddProposalContent={canAddProposalContent}
          isReadOnlyMode={isReadOnlyMode}
          storyContent={storyContent}
          storyContentArray={selectStoryContentFormats(storyContent)}
          messages={messages}
          story={story}
          storyContents={storyContents}
          handleSaveSpacing={handleSaveSpacing}
          handleFormatClick={handleFormatClick}
          handleColorClick={handleColorClick}
          handleOrderClick={handleOrderClick}
          handleAddSlide={handleAddSlide}
          handleUpdateModal={handleUpdateModal}
          handlePreviewSection={handlePreviewSection}
          modalMessages={modalMessages}
        />
      </div>
      <AddContent
        isLast={props.isLast}
        totalStoryContents={props.totalStoryContents}
        nextStoryContentOrder={props.nextStoryContentOrder}
        isReadOnlyMode={isReadOnlyMode}
        messages={messages}
        handleAddModal={handleAddModal}
      />
    </div>
  );
});

TinyMCEStoryEditor.displayName = 'TinyMCEStoryEditor';
